import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton, Divider } from "@/pages/auth/AuthShell";

export default function Register() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applyMe } = useAuth();
  const next = params.get("next") || "/app/onboarding";
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError("Please accept the Terms and Privacy Policy."); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      applyMe(data);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start your free trial — set up takes a few minutes." testId="register-page">
      <GoogleButton next={next} />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <Alert variant="destructive" data-testid="register-error"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="space-y-1.5"><Label htmlFor="name">Full name</Label><Input id="name" required value={form.name} onChange={set("name")} className="h-11 rounded-xl" data-testid="register-name-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="email">Work email</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-11 rounded-xl" data-testid="register-email-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="password">Password</Label><Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="h-11 rounded-xl" data-testid="register-password-input" /><p className="text-xs text-slate-500">At least 8 characters.</p></div>
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="register-terms-checkbox" />
          <span>I agree to the <Link to="/terms" className="text-[#00A85C] hover:underline">Terms</Link> and <Link to="/privacy" className="text-[#00A85C] hover:underline">Privacy Policy</Link>.</span>
        </label>
        <Button disabled={busy} className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="register-submit-btn">{busy ? "Creating account…" : "Create account"}</Button>
      </form>
      <p className="mt-6 text-sm text-slate-600">Already have an account? <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-semibold text-[#00A85C] hover:underline" data-testid="login-link">Log in</Link></p>
    </AuthShell>
  );
}
